import React from 'react';

type FeatureCard = {
  icon: string;
  title: string;
  description: string;
};

const FEATURE_CARDS: FeatureCard[] = [
  {
    icon: 'mdi-calendar-check',
    title: 'Session booking',
    description:
      'List training sessions, set places and prices, and let members book in two taps with Apple Pay or Google Pay. Cancellations refund automatically when enabled for the session.',
  },
  {
    icon: 'mdi-account-group',
    title: 'Membership management',
    description:
      'Rolling 12-month memberships, ideal for endurance clubs. Trial periods managed, subs collected for you, and unpaid accounts frozen automatically.',
  },
  {
    icon: 'mdi-web',
    title: 'Templated professional website',
    description:
      "A professional club website from a proven template — your branding, your domain, with sessions and news kept in sync with the app.",
  },
  {
    icon: 'mdi-message-badge',
    title: 'Blogs, Posts & Messaging',
    description:
      'Three ways to communicate, baked in: Blogs publish publicly on your website, Posts reach signed-in members in the app, and Messaging — DMs, group chats and communities.',
  },
  {
    icon: 'mdi-trophy',
    title: 'Club champs',
    description:
      'Run a season-long championship with listed races and live standings — a bit of friendly competition and rivalry.',
  },
  {
    icon: 'mdi-tag',
    title: 'Club secrets',
    description:
      'Share club secrets — lock-up codes, discount codes and member perks — with exactly who should see them: admins, coaches, paid-up members or specific individuals.',
  },
];

/**
 * Features grid — source: mockup L302–341.
 *
 * On phones only the first three cards show, with a fade and a "Show more"
 * button (progressive disclosure — the collapsed state is applied purely via
 * CSS inside the narrow media query, so wider viewports always see all six).
 */
const FeaturesGrid = (): React.JSX.Element => {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <section className="oq-features anchor-section" id="features">
      <div className="oq-container oq-features__inner">
        <div className="oq-features__intro">
          <div className="oq-features__kicker oq-bebas">ONE APP · EVERYTHING</div>
          <h2 className="oq-features__title oq-bebas">Everything your club needs to run</h2>
          <p className="oq-features__subtitle">
            Stop stitching together a booking tool, a payments provider, a website and five chat
            groups. Olympiq is one membership management system your whole club runs on.
          </p>
        </div>
        <div
          id="features-grid"
          className={`oq-features__grid${expanded ? '' : ' oq-features__grid--collapsed'}`}
        >
          {FEATURE_CARDS.map((card) => (
            <div className="oq-features__card" key={card.title}>
              <div className="oq-features__card-icon">
                <i className={`mdi ${card.icon}`} aria-hidden="true" />
              </div>
              <h3 className="oq-features__card-title oq-bebas">{card.title}</h3>
              <p className="oq-features__card-body">{card.description}</p>
            </div>
          ))}
        </div>
        {!expanded && (
          <button
            type="button"
            className="oq-features__more"
            aria-expanded={false}
            aria-controls="features-grid"
            onClick={() => setExpanded(true)}
          >
            Show more <i className="mdi mdi-chevron-down" aria-hidden="true" />
          </button>
        )}
      </div>
    </section>
  );
};

export default FeaturesGrid;
